import { z } from 'zod';

/**
 * Therapist Agreement digital signature payload (POST /api/v1/therapist/sign-agreement).
 * Client sends a base64-encoded PNG of the drawn signature; we store it encrypted.
 */
export const SignAgreementSchema = z.object({
  agreementVersion: z.string().default('t-v1.0'),
  signatureBase64: z
    .string()
    .min(100, 'Signature image is suspiciously small')
    .max(500_000, 'Signature image exceeds 500 KB')
    .regex(/^data:image\/png;base64,/, 'Signature must be a PNG data URL'),
});
export type SignAgreementInput = z.infer<typeof SignAgreementSchema>;

/** Client ToS acceptance (POST /api/v1/client/accept-tos). */
export const AcceptTosSchema = z.object({
  tosVersion: z.string().default('c-v1.0'),
  deviceId: z.string().min(1).max(120),
});
export type AcceptTosInput = z.infer<typeof AcceptTosSchema>;

/** Read model returned by GET endpoints so the UI can render "signed on ...". */
export const AgreementRecordSchema = z.object({
  agreementVersion: z.string(),
  signedAt: z.string().datetime(),
});
export type AgreementRecord = z.infer<typeof AgreementRecordSchema>;

/**
 * Current legal versions the apps must hold. Bumping one of these is what makes
 * every existing signature stale and re-triggers the gate — so they live beside
 * the schemas rather than in each app, where they would drift apart.
 */
export const CURRENT_THERAPIST_AGREEMENT_VERSION = 't-v1.0';
export const CURRENT_CLIENT_TOS_VERSION = 'c-v1.0';

/**
 * GET /api/v1/agreements/status — what the signed-in user still owes us.
 * `signed` is false both when nothing was ever signed AND when the stored
 * version is older than the current one, so a new contract version forces a
 * re-signature without any extra client-side comparison.
 */
export const AgreementStatusSchema = z.object({
  role: z.enum(['CLIENT', 'THERAPIST', 'ADMIN']),
  requiredVersion: z.string().nullable(),
  signedVersion: z.string().nullable(),
  signedAt: z.string().datetime().nullable(),
  signed: z.boolean(),
});
export type AgreementStatus = z.infer<typeof AgreementStatusSchema>;
